import { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { getSubstitutionStatsDirect, type SubstitutionStatsData } from '@timetable/api';

interface SubstitutionStatsReportProps {
  startDate: Date | null;
  endDate: Date | null;
}

export function SubstitutionStatsReport({ startDate, endDate }: SubstitutionStatsReportProps) {
  const { data: statsData, isLoading, error } = useQuery({
    queryKey: ['substitution-stats-report', startDate, endDate],
    queryFn: () => getSubstitutionStatsDirect(
      startDate?.toISOString().split('T')[0],
      endDate?.toISOString().split('T')[0]
    ),
    enabled: !!startDate && !!endDate,
  });

  const { chartData, summary } = useMemo(() => {
    if (!statsData || statsData.length === 0) {
      return {
        chartData: [],
        summary: {
          totalSubstitutions: 0,
          totalAuto: 0,
          totalManual: 0,
          avgResponseTime: 0,
        },
      };
    }

    const totalSubstitutions = statsData.reduce((sum, t) => sum + t.total_substitutions, 0);
    const totalAuto = statsData.reduce((sum, t) => sum + t.auto_assignments, 0);
    const totalManual = statsData.reduce((sum, t) => sum + t.manual_assignments, 0);
    const avgResponseTime =
      statsData.reduce((sum, t) => sum + t.avg_response_time_minutes, 0) / statsData.length;

    const chartData = statsData
      .sort((a, b) => b.total_substitutions - a.total_substitutions)
      .slice(0, 10) // Top 10 teachers
      .map(teacher => ({
        name: teacher.teacher_name,
        auto: teacher.auto_assignments,
        manual: teacher.manual_assignments,
      }));

    return {
      chartData,
      summary: {
        totalSubstitutions,
        totalAuto,
        totalManual,
        avgResponseTime: Math.round(avgResponseTime * 10) / 10,
      },
    };
  }, [statsData]);

  const formatResponseTime = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load substitution statistics. Please try again.
      </Alert>
    );
  }

  if (!statsData || statsData.length === 0) {
    return (
      <EmptyState
        title="No Data Available"
        description="No substitution data found for the selected date range."
      />
    );
  }

  return (
    <Box>
      {/* Summary Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Substitution Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Substitutions
            </Typography>
            <Typography variant="h4">{summary.totalSubstitutions}</Typography>
            <Typography variant="caption" color="text.secondary">
              assignments
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Auto Assignments
            </Typography>
            <Typography variant="h4">{summary.totalAuto}</Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.totalSubstitutions > 0
                ? `${((summary.totalAuto / summary.totalSubstitutions) * 100).toFixed(1)}%`
                : '0%'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Manual Assignments
            </Typography>
            <Typography variant="h4">{summary.totalManual}</Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.totalSubstitutions > 0
                ? `${((summary.totalManual / summary.totalSubstitutions) * 100).toFixed(1)}%`
                : '0%'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Avg Response Time
            </Typography>
            <Typography variant="h4">{formatResponseTime(summary.avgResponseTime)}</Typography>
            <Typography variant="caption" color="text.secondary">
              assignment to notification
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Bar Chart - Top 10 Teachers */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top 10 Substitute Teachers
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis label={{ value: 'Number of Substitutions', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="auto" name="Auto Assigned" fill="#2e7d32" stackId="a" />
            <Bar dataKey="manual" name="Manual Assigned" fill="#ed6c02" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Detailed Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Substitution Details by Teacher
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher Name</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Auto</TableCell>
                <TableCell align="right">Manual</TableCell>
                <TableCell align="right">Avg Response Time</TableCell>
                <TableCell align="center">Assignment Method</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statsData
                .sort((a, b) => b.total_substitutions - a.total_substitutions)
                .map((teacher) => {
                  const autoPercentage =
                    teacher.total_substitutions > 0
                      ? ((teacher.auto_assignments / teacher.total_substitutions) * 100).toFixed(0)
                      : 0;
                  return (
                    <TableRow key={teacher.teacher_id}>
                      <TableCell>{teacher.teacher_name}</TableCell>
                      <TableCell align="right">
                        <strong>{teacher.total_substitutions}</strong>
                      </TableCell>
                      <TableCell align="right">{teacher.auto_assignments}</TableCell>
                      <TableCell align="right">{teacher.manual_assignments}</TableCell>
                      <TableCell align="right">
                        {formatResponseTime(teacher.avg_response_time_minutes)}
                      </TableCell>
                      <TableCell align="center">
                        {teacher.auto_assignments > teacher.manual_assignments ? (
                          <Chip label={`${autoPercentage}% Auto`} color="success" size="small" />
                        ) : (
                          <Chip
                            label={`${100 - Number(autoPercentage)}% Manual`}
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
